import React, { useState, useEffect, useRef } from 'react';
import { Input } from './input';
import { Card, CardContent } from './card';
import { ChevronDown, Search, Globe, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface Country {
  code: string;
  name: string;
  nameEn: string;
  continent: string;
  flag: string;
}

interface CountrySelectorProps {
  value: string;
  onChange: (code: string, name: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const countries: Country[] = [
  // Europe
  { code: 'CZ', name: 'Česká republika', nameEn: 'Czech Republic', continent: 'Europe', flag: '🇨🇿' },
  { code: 'SK', name: 'Slovensko', nameEn: 'Slovakia', continent: 'Europe', flag: '🇸🇰' },
  { code: 'DE', name: 'Německo', nameEn: 'Germany', continent: 'Europe', flag: '🇩🇪' },
  { code: 'AT', name: 'Rakousko', nameEn: 'Austria', continent: 'Europe', flag: '🇦🇹' },
  { code: 'PL', name: 'Polsko', nameEn: 'Poland', continent: 'Europe', flag: '🇵🇱' },
  { code: 'HU', name: 'Maďarsko', nameEn: 'Hungary', continent: 'Europe', flag: '🇭🇺' },
  { code: 'SI', name: 'Slovinsko', nameEn: 'Slovenia', continent: 'Europe', flag: '🇸🇮' },
  { code: 'HR', name: 'Chorvatsko', nameEn: 'Croatia', continent: 'Europe', flag: '🇭🇷' },
  { code: 'IT', name: 'Itálie', nameEn: 'Italy', continent: 'Europe', flag: '🇮🇹' },
  { code: 'FR', name: 'Francie', nameEn: 'France', continent: 'Europe', flag: '🇫🇷' },
  { code: 'ES', name: 'Španělsko', nameEn: 'Spain', continent: 'Europe', flag: '🇪🇸' },
  { code: 'PT', name: 'Portugalsko', nameEn: 'Portugal', continent: 'Europe', flag: '🇵🇹' },
  { code: 'NL', name: 'Nizozemsko', nameEn: 'Netherlands', continent: 'Europe', flag: '🇳🇱' },
  { code: 'BE', name: 'Belgie', nameEn: 'Belgium', continent: 'Europe', flag: '🇧🇪' },
  { code: 'LU', name: 'Lucembursko', nameEn: 'Luxembourg', continent: 'Europe', flag: '🇱🇺' },
  { code: 'CH', name: 'Švýcarsko', nameEn: 'Switzerland', continent: 'Europe', flag: '🇨🇭' },
  { code: 'LI', name: 'Lichtenštejnsko', nameEn: 'Liechtenstein', continent: 'Europe', flag: '🇱🇮' },
  { code: 'DK', name: 'Dánsko', nameEn: 'Denmark', continent: 'Europe', flag: '🇩🇰' },
  { code: 'SE', name: 'Švédsko', nameEn: 'Sweden', continent: 'Europe', flag: '🇸🇪' },
  { code: 'NO', name: 'Norsko', nameEn: 'Norway', continent: 'Europe', flag: '🇳🇴' },
  { code: 'FI', name: 'Finsko', nameEn: 'Finland', continent: 'Europe', flag: '🇫🇮' },
  { code: 'IS', name: 'Island', nameEn: 'Iceland', continent: 'Europe', flag: '🇮🇸' },
  { code: 'IE', name: 'Irsko', nameEn: 'Ireland', continent: 'Europe', flag: '🇮🇪' },
  { code: 'GB', name: 'Velká Británie', nameEn: 'United Kingdom', continent: 'Europe', flag: '🇬🇧' },
  { code: 'MT', name: 'Malta', nameEn: 'Malta', continent: 'Europe', flag: '🇲🇹' },
  { code: 'CY', name: 'Kypr', nameEn: 'Cyprus', continent: 'Europe', flag: '🇨🇾' },
  { code: 'GR', name: 'Řecko', nameEn: 'Greece', continent: 'Europe', flag: '🇬🇷' },
  { code: 'BG', name: 'Bulharsko', nameEn: 'Bulgaria', continent: 'Europe', flag: '🇧🇬' },
  { code: 'RO', name: 'Rumunsko', nameEn: 'Romania', continent: 'Europe', flag: '🇷🇴' },
  { code: 'EE', name: 'Estonsko', nameEn: 'Estonia', continent: 'Europe', flag: '🇪🇪' },
  { code: 'LV', name: 'Lotyšsko', nameEn: 'Latvia', continent: 'Europe', flag: '🇱🇻' },
  { code: 'LT', name: 'Litva', nameEn: 'Lithuania', continent: 'Europe', flag: '🇱🇹' },
  { code: 'RS', name: 'Srbsko', nameEn: 'Serbia', continent: 'Europe', flag: '🇷🇸' },
  { code: 'BA', name: 'Bosna a Hercegovina', nameEn: 'Bosnia and Herzegovina', continent: 'Europe', flag: '🇧🇦' },
  { code: 'ME', name: 'Černá Hora', nameEn: 'Montenegro', continent: 'Europe', flag: '🇲🇪' },
  { code: 'MK', name: 'Severní Makedonie', nameEn: 'North Macedonia', continent: 'Europe', flag: '🇲🇰' },
  { code: 'AL', name: 'Albánie', nameEn: 'Albania', continent: 'Europe', flag: '🇦🇱' },
  { code: 'XK', name: 'Kosovo', nameEn: 'Kosovo', continent: 'Europe', flag: '🇽🇰' },
  { code: 'MD', name: 'Moldavsko', nameEn: 'Moldova', continent: 'Europe', flag: '🇲🇩' },
  { code: 'UA', name: 'Ukrajina', nameEn: 'Ukraine', continent: 'Europe', flag: '🇺🇦' },
  { code: 'BY', name: 'Bělorusko', nameEn: 'Belarus', continent: 'Europe', flag: '🇧🇾' },
  { code: 'RU', name: 'Rusko', nameEn: 'Russia', continent: 'Europe', flag: '🇷🇺' },
  
  // Asia
  { code: 'CN', name: 'Čína', nameEn: 'China', continent: 'Asia', flag: '🇨🇳' },
  { code: 'JP', name: 'Japonsko', nameEn: 'Japan', continent: 'Asia', flag: '🇯🇵' },
  { code: 'KR', name: 'Jižní Korea', nameEn: 'South Korea', continent: 'Asia', flag: '🇰🇷' },
  { code: 'IN', name: 'Indie', nameEn: 'India', continent: 'Asia', flag: '🇮🇳' },
  { code: 'ID', name: 'Indonésie', nameEn: 'Indonesia', continent: 'Asia', flag: '🇮🇩' },
  { code: 'TH', name: 'Thajsko', nameEn: 'Thailand', continent: 'Asia', flag: '🇹🇭' },
  { code: 'VN', name: 'Vietnam', nameEn: 'Vietnam', continent: 'Asia', flag: '🇻🇳' },
  { code: 'MY', name: 'Malajsie', nameEn: 'Malaysia', continent: 'Asia', flag: '🇲🇾' },
  { code: 'SG', name: 'Singapur', nameEn: 'Singapore', continent: 'Asia', flag: '🇸🇬' },
  { code: 'PH', name: 'Filipíny', nameEn: 'Philippines', continent: 'Asia', flag: '🇵🇭' },
  { code: 'TR', name: 'Turecko', nameEn: 'Turkey', continent: 'Asia', flag: '🇹🇷' },
  { code: 'SA', name: 'Saúdská Arábie', nameEn: 'Saudi Arabia', continent: 'Asia', flag: '🇸🇦' },
  { code: 'AE', name: 'Spojené arabské emiráty', nameEn: 'United Arab Emirates', continent: 'Asia', flag: '🇦🇪' },
  { code: 'IL', name: 'Izrael', nameEn: 'Israel', continent: 'Asia', flag: '🇮🇱' },
  
  // North America
  { code: 'US', name: 'Spojené státy americké', nameEn: 'United States', continent: 'North America', flag: '🇺🇸' },
  { code: 'CA', name: 'Kanada', nameEn: 'Canada', continent: 'North America', flag: '🇨🇦' },
  { code: 'MX', name: 'Mexiko', nameEn: 'Mexico', continent: 'North America', flag: '🇲🇽' },
  
  // South America
  { code: 'BR', name: 'Brazílie', nameEn: 'Brazil', continent: 'South America', flag: '🇧🇷' },
  { code: 'AR', name: 'Argentina', nameEn: 'Argentina', continent: 'South America', flag: '🇦🇷' },
  { code: 'CL', name: 'Chile', nameEn: 'Chile', continent: 'South America', flag: '🇨🇱' },
  { code: 'CO', name: 'Kolumbie', nameEn: 'Colombia', continent: 'South America', flag: '🇨🇴' },
  { code: 'PE', name: 'Peru', nameEn: 'Peru', continent: 'South America', flag: '🇵🇪' },
  
  // Africa
  { code: 'ZA', name: 'Jihoafrická republika', nameEn: 'South Africa', continent: 'Africa', flag: '🇿🇦' },
  { code: 'EG', name: 'Egypt', nameEn: 'Egypt', continent: 'Africa', flag: '🇪🇬' },
  { code: 'NG', name: 'Nigérie', nameEn: 'Nigeria', continent: 'Africa', flag: '🇳🇬' },
  { code: 'KE', name: 'Keňa', nameEn: 'Kenya', continent: 'Africa', flag: '🇰🇪' },
  { code: 'MA', name: 'Maroko', nameEn: 'Morocco', continent: 'Africa', flag: '🇲🇦' },
  
  // Oceania
  { code: 'AU', name: 'Austrálie', nameEn: 'Australia', continent: 'Oceania', flag: '🇦🇺' },
  { code: 'NZ', name: 'Nový Zéland', nameEn: 'New Zealand', continent: 'Oceania', flag: '🇳🇿' },
];

const CountrySelector: React.FC<CountrySelectorProps> = ({
  value,
  onChange,
  placeholder = "Vyberte zemi...",
  className,
  disabled = false
}) => {
  const { i18n } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize selected country from value
  useEffect(() => {
    if (value) {
      const country = countries.find(c => c.code === value);
      setSelectedCountry(country || null);
    } else {
      setSelectedCountry(null);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter countries based on search query
  const filteredCountries = countries.filter(country => {
    const query = searchQuery.toLowerCase();
    const currentLang = i18n.language;
    
    if (currentLang === 'cs') {
      return country.name.toLowerCase().includes(query) || 
             country.code.toLowerCase().includes(query);
    } else {
      return country.nameEn.toLowerCase().includes(query) || 
             country.code.toLowerCase().includes(query);
    }
  });

  // Group countries by continent
  const groupedCountries = filteredCountries.reduce((acc, country) => {
    if (!acc[country.continent]) {
      acc[country.continent] = [];
    }
    acc[country.continent].push(country);
    return acc;
  }, {} as Record<string, Country[]>);

  // Sort continents with Europe first
  // const sortedContinents = Object.keys(groupedCountries).sort((a, b) => {
  //   if (a === 'Europe') return -1;
  //   if (b === 'Europe') return 1;
  //   return a.localeCompare(b);
  // });

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchQuery('');
    onChange(country.code, i18n.language === 'cs' ? country.name : country.nameEn);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        // Focus search input when opening
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
      }
    }
  };

  const displayName = selectedCountry 
    ? (i18n.language === 'cs' ? selectedCountry.name : selectedCountry.nameEn)
    : '';

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <div
        className={cn(
          "flex items-center justify-between w-full px-3 py-2 text-sm border rounded-md cursor-pointer bg-background",
          "hover:border-gray-300 focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-500",
          disabled && "opacity-50 cursor-not-allowed bg-gray-50",
          className
        )}
        onClick={handleInputClick}
      >
        <div className="flex items-center flex-1 min-w-0">
          {selectedCountry && (
            <span className="mr-2 text-lg">{selectedCountry.flag}</span>
          )}
          <span className={cn(
            "truncate",
            !displayName && "text-muted-foreground"
          )}>
            {displayName || placeholder}
          </span>
        </div>
        <ChevronDown 
          className={cn(
            "h-4 w-4 text-gray-400 transition-transform",
            isOpen && "transform rotate-180"
          )} 
        />
      </div>

      {isOpen && !disabled && (
        <Card className="absolute z-50 w-full mt-1 border shadow-lg">
          <CardContent className="p-0">
            <div className="p-3 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Hledat zemi..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {Object.keys(groupedCountries).length === 0 ? (
                <div className="p-4 text-center text-slate-500">
                  <Search className="h-5 w-5 mx-auto mb-2 text-slate-400" />
                  <p className="text-sm">
                    {searchQuery ? 'No results found' : 'No countries available'}
                  </p>
                </div>
              ) : (
                Object.entries(groupedCountries).map(([continent, countries]) => (
                  <div key={continent} className="border-b last:border-b-0">
                    <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                      <Globe className="inline h-3 w-3 mr-1" />
                      {continent}
                    </div>
                    {countries.map((country) => (
                      <div
                        key={country.code}
                        className={cn(
                          "flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-slate-50",
                          selectedCountry?.code === country.code && "bg-orange-50 text-orange-600"
                        )}
                        onClick={() => handleCountrySelect(country)}
                      >
                        <span className="mr-3 text-lg">{country.flag}</span>
                        <span className="flex-1">
                          {i18n.language === 'cs' ? country.name : country.nameEn}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          {country.code}
                        </span>
                        {selectedCountry?.code === country.code && (
                          <Check className="h-4 w-4 ml-2 text-orange-600" />
                        )}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CountrySelector;